'use client';

import { useEffect, useState } from 'react';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { VelocityWindow } from 'src/window/velocityWindow';

const TICK_MS = 1000;

/**
 * Non-reactive read of the on-chain clock timestamp (unix seconds) at the default
 * commitment level. Returns `undefined` until the chain clock has been observed. Use
 * this in imperative code (instruction builders, event handlers); use `useChainClockTs`
 * when a component needs to re-render as time advances.
 */
export function getChainClockTs(): number | undefined {
	return VelocityWindow.chainClock.getState(Env.defaultCommitmentLevel)?.ts;
}

/**
 * Reactive accessor for the on-chain clock timestamp (unix seconds) at the default
 * commitment level. Re-renders roughly once per second so time-based derivations
 * (e.g. order expiry) stay current without needing an account/data update — plain
 * account subscriptions don't fire just because time has passed.
 *
 * Returns `undefined` until the chain clock has been observed. React bails out of
 * re-renders when the value is unchanged, so consumers only update when `ts` advances.
 */
export function useChainClockTs(): number | undefined {
	const [ts, setTs] = useState<number | undefined>(getChainClockTs);

	useEffect(() => {
		const read = () => setTs(getChainClockTs());

		read();
		const interval = setInterval(read, TICK_MS);
		return () => clearInterval(interval);
	}, []);

	return ts;
}
